import React, { useState, useEffect, useCallback } from "react";
import I18n from "i18n";
import vent from "libraries/vent";

function useRatingShortcuts(handlers: {
  positive: () => void;
  neutral: () => void;
  negative: () => void;
}) {
  useEffect(() => {
    type ShortcutHandler = "positive" | "neutral" | "negative";
    const keyMap: Record<string, ShortcutHandler> = {
      "+": "positive",
      "3": "positive",
      "=": "neutral",
      "2": "neutral",
      "-": "negative",
      "1": "negative",
    };

    function onKeyDown(e: KeyboardEvent) {
      const activeElem = document.activeElement;
      const isInputFocused =
        activeElem &&
        (
          activeElem.tagName === "INPUT" ||
          activeElem.tagName === "TEXTAREA" ||
          (activeElem as HTMLElement).isContentEditable
        );

      if (isInputFocused) {return;}
      const handlerKey = keyMap[e.key];
      if (handlerKey) {handlers[handlerKey]();}
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handlers]);
}

interface Props {
  selectedAgentResults: any; // Backbone.Model
  ratingValue: number | null;
}

type RatingType = "positive" | "neutral" | "negative" | null;

function ratingTypeFromValue(val: number | null | undefined): RatingType {
  if (val === null || val === undefined) {return null;}
  if (val > 0) {return "positive";}
  if (val < 0) {return "negative";}
  return "neutral";
}

function valueFromRatingType(type: RatingType): number | null {
  if (type === "positive") {return 10;}
  if (type === "neutral") {return 0;}
  if (type === "negative") {return -10;}
  return null;
}

function haveSameRating(collection: any): boolean {
  if (!collection || collection.length === 0) {return true;}
  if (collection.length === 1) {return true;}
  const first = collection.first ? collection.first() : null;
  const firstValue = first ? first.get("rating_value") : null;
  if (collection.every) {
    return collection.every((model: any) => model.get("rating_value") === firstValue);
  }
  return true;
}

function currentRatingFromCollection(collection: any): number | null {
  if (!collection || collection.length === 0) {return null;}
  if (!haveSameRating(collection)) {return null;}
  const first = collection.first ? collection.first() : null;
  return first ? first.get("rating_value") : null;
}

function collectAgentResultIds(collection: any, fallbackModel: any): number[] {
  if (collection && collection.map) {
    return collection.map((model: any) => model.get("id"));
  }
  if (fallbackModel) {
    return [fallbackModel.get ? fallbackModel.get("id") : fallbackModel.id];
  }
  return [];
}

export default function DetailViewRatingBtn({ selectedAgentResults, ratingValue }: Props) {
  const [active, setActive] = useState<RatingType>(ratingTypeFromValue(ratingValue));
  // Sync UI with prop
  useEffect(() => {
    setActive(ratingTypeFromValue(ratingValue));
  }, [ratingValue]);

  const handleToggle = useCallback(
    (type: RatingType) => {
      setActive(prevActive => {
        const collection = selectedAgentResults.collection;

        if (collection && collection.each) {
          collection.each((model: any) => {
            if (model.store) {model.store();}
          });
        }

        const currentRating = currentRatingFromCollection(collection) ?? ratingValue;
        const targetValue = valueFromRatingType(type);
        const newValue = currentRating === targetValue ? null : targetValue;
        const newType = ratingTypeFromValue(newValue);

        if (collection && collection.each) {
          collection.each((model: any) => {
            if (model.set) {model.set("rating_value", newValue);}
          });
        }

        if (selectedAgentResults.set) {
          selectedAgentResults.set("rating_value", newValue);
        }

        vent.trigger(
          "changed:resultSentiment",
          collectAgentResultIds(collection, selectedAgentResults),
          newValue
        );

        selectedAgentResults.save(
          { agent_result: { rating_value: newValue } },
          {
            patch: true,
            error: () => {
              collection?.each?.((model: any) => { if (model.restore) {model.restore();} });
              if (selectedAgentResults.restore) {
                selectedAgentResults.restore();
              }
              const restoredRating = currentRatingFromCollection(collection) ?? ratingValue;
              setActive(ratingTypeFromValue(restoredRating));
            },
          }
        );
        return newType;
      });
    },
    [selectedAgentResults, ratingValue]
  );

  const handlePositive = () => handleToggle("positive");
  const handleNeutral = () => handleToggle("neutral");
  const handleNegative = () => handleToggle("negative");

  useRatingShortcuts({
    positive: handlePositive,
    neutral: handleNeutral,
    negative: handleNegative,
  });

  const getButtonClass = (type: RatingType) =>
    `button-group__item clip-actions__rate clip-actions__rate--${type}` +
    (active === type ? " is-active" : "");

  return (
    <div data-ratings-partial className="clip-actions__rating">
      <div className="button-group button-group--small button-group--horizontal clip-actions__rate-group">
        <a
          className={getButtonClass("negative")}
          id="rate-negative"
          onClick={handleNegative}
        >
          <i className="clip-actions__rate-icon"></i>{" "}
          <span className="handheld-hidden">
            {I18n.t("webapp.agents.results.result_details.rating.negative")}
          </span>
        </a>
        <a
          className={getButtonClass("neutral")}
          id="rate-neutral"
          onClick={handleNeutral}
        >
          <i className="clip-actions__rate-icon"></i>{" "}
          <span className="handheld-hidden">
            {I18n.t("webapp.agents.results.result_details.rating.neutral")}
          </span>
        </a>
        <a
          className={getButtonClass("positive")}
          id="rate-positive"
          onClick={handlePositive}
        >
          <i className="clip-actions__rate-icon"></i>{" "}
          <span className="handheld-hidden">
            {I18n.t("webapp.agents.results.result_details.rating.positive")}
          </span>
        </a>
      </div>
    </div>
  );
}
